import { useState } from "react";
import "@/App.css";
import { Eye, LogOut, LayoutDashboard, Users, Settings, FileDown, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

// Mock user data - 15 realistic Indonesian users
const mockUsers = [
  {
    id: 1,
    fullName: "Budi Santoso",
    email: "budi.santoso@pertamina.com",
    organization: "PT Pertamina (Persero)",
    role: "Sustainability Manager",
    phone: "+62 812-3456-7890",
    membershipType: "Professional",
    industry: "Energy",
    yearsExperience: "8",
    paymentStatus: "Success",
    registeredDate: "2024-01-15"
  },
  {
    id: 2,
    fullName: "Siti Nurhaliza",
    email: "siti.nurhaliza@unilever.co.id",
    organization: "Unilever Indonesia",
    role: "CSR Director",
    phone: "+62 813-9876-5432",
    membershipType: "Corporate",
    industry: "Consumer Goods",
    yearsExperience: "12",
    paymentStatus: "Success",
    registeredDate: "2024-01-18"
  },
  {
    id: 3,
    fullName: "Ahmad Rizki",
    email: "ahmad.rizki@antam.com",
    organization: "PT Aneka Tambang Tbk",
    role: "HSE Officer",
    phone: "+62 856-1234-5678",
    membershipType: "Professional",
    industry: "Mining",
    yearsExperience: "5",
    paymentStatus: "Pending",
    registeredDate: "2024-01-20"
  },
  {
    id: 4,
    fullName: "Dewi Lestari",
    email: "dewi.lestari@ui.ac.id",
    organization: "Universitas Indonesia",
    role: "Environmental Researcher",
    phone: "+62 821-5555-6666",
    membershipType: "Academic",
    industry: "Education",
    yearsExperience: "10",
    paymentStatus: "Success",
    registeredDate: "2024-01-22"
  },
  {
    id: 5,
    fullName: "Eko Prasetyo",
    email: "eko.prasetyo@pln.co.id",
    organization: "PT PLN (Persero)",
    role: "Renewable Energy Specialist",
    phone: "+62 877-9999-8888",
    membershipType: "Professional",
    industry: "Energy",
    yearsExperience: "6",
    paymentStatus: "Success",
    registeredDate: "2024-01-25"
  },
  {
    id: 6,
    fullName: "Rina Wijaya",
    email: "rina.wijaya@walhi.or.id",
    organization: "WALHI (Indonesian Forum for Environment)",
    role: "Climate Advocacy Manager",
    phone: "+62 818-7777-3333",
    membershipType: "NGO",
    industry: "Non-Profit",
    yearsExperience: "7",
    paymentStatus: "Pending",
    registeredDate: "2024-01-28"
  },
  {
    id: 7,
    fullName: "Agus Setiawan",
    email: "agus.setiawan@astra.co.id",
    organization: "PT Astra International Tbk",
    role: "Sustainability Coordinator",
    phone: "+62 852-4444-1111",
    membershipType: "Corporate",
    industry: "Automotive",
    yearsExperience: "4",
    paymentStatus: "Success",
    registeredDate: "2024-02-01"
  },
  {
    id: 8,
    fullName: "Maya Kusuma",
    email: "maya.kusuma@danone.co.id",
    organization: "Danone Indonesia",
    role: "Circular Economy Lead",
    phone: "+62 819-2222-9999",
    membershipType: "Professional",
    industry: "Food & Beverage",
    yearsExperience: "9",
    paymentStatus: "Success",
    registeredDate: "2024-02-03"
  },
  {
    id: 9,
    fullName: "Hendra Gunawan",
    email: "hendra.gunawan@kemenklh.go.id",
    organization: "Kementerian Lingkungan Hidup",
    role: "Policy Analyst",
    phone: "+62 811-6666-7777",
    membershipType: "Government",
    industry: "Public Sector",
    yearsExperience: "11",
    paymentStatus: "Success",
    registeredDate: "2024-02-05"
  },
  {
    id: 10,
    fullName: "Lina Mariana",
    email: "lina.mariana@wwf.or.id",
    organization: "WWF Indonesia",
    role: "Conservation Program Manager",
    phone: "+62 857-3333-4444",
    membershipType: "NGO",
    industry: "Non-Profit",
    yearsExperience: "8",
    paymentStatus: "Failed",
    registeredDate: "2024-02-08"
  },
  {
    id: 11,
    fullName: "Rudi Hermawan",
    email: "rudi.hermawan@bukalapak.com",
    organization: "Bukalapak",
    role: "ESG Compliance Officer",
    phone: "+62 878-8888-5555",
    membershipType: "Professional",
    industry: "Technology",
    yearsExperience: "3",
    paymentStatus: "Pending",
    registeredDate: "2024-02-10"
  },
  {
    id: 12,
    fullName: "Kartika Sari",
    email: "kartika.sari@sinarmas.co.id",
    organization: "Sinar Mas Group",
    role: "Head of Sustainability",
    phone: "+62 859-1111-2222",
    membershipType: "Corporate",
    industry: "Conglomerate",
    yearsExperience: "15",
    paymentStatus: "Success",
    registeredDate: "2024-02-12"
  },
  {
    id: 13,
    fullName: "Yudi Prasetya",
    email: "yudi.prasetya@itb.ac.id",
    organization: "Institut Teknologi Bandung",
    role: "Sustainable Engineering Lecturer",
    phone: "+62 822-7777-8888",
    membershipType: "Academic",
    industry: "Education",
    yearsExperience: "13",
    paymentStatus: "Success",
    registeredDate: "2024-02-14"
  },
  {
    id: 14,
    fullName: "Fitri Handayani",
    email: "fitri.handayani@gojek.com",
    organization: "Gojek Indonesia",
    role: "Green Initiative Manager",
    phone: "+62 838-9999-6666",
    membershipType: "Professional",
    industry: "Technology",
    yearsExperience: "5",
    paymentStatus: "Success",
    registeredDate: "2024-02-16"
  },
  {
    id: 15,
    fullName: "Bambang Suryanto",
    email: "bambang.suryanto@medco.com",
    organization: "Medco Energi",
    role: "Environmental Compliance Director",
    phone: "+62 812-5555-3333",
    membershipType: "Corporate",
    industry: "Energy",
    yearsExperience: "16",
    paymentStatus: "Success",
    registeredDate: "2024-02-18"
  }
];

const LoginPage = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();
    onLogin();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-emerald-50/30 to-slate-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-lg" data-testid="login-card">
        <CardHeader className="space-y-3 text-center">
          <div className="mx-auto w-16 h-16 bg-emerald-600 rounded-xl flex items-center justify-center mb-2">
            <Users className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="text-2xl font-bold text-slate-800">IS2P Admin Portal</CardTitle>
          <CardDescription className="text-slate-600">Indonesian Society of Sustainability Professionals</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-700">Email Address</Label>
              <Input
                id="email"
                type="email"
                placeholder="admin@is2p.org"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="border-slate-300 focus:border-emerald-500 focus:ring-emerald-500"
                data-testid="email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-700">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="border-slate-300 focus:border-emerald-500 focus:ring-emerald-500"
                data-testid="password-input"
              />
            </div>
            <Button
              type="submit"
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-medium py-2.5 transition-colors"
              data-testid="login-button"
            >
              Login to Dashboard
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

const Sidebar = ({ currentMenu, onMenuChange, onLogout }) => {
  const menuItems = [
    { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
    { id: "users", label: "Data User", icon: Users },
    { id: "settings", label: "Settings", icon: Settings },
  ];

  return (
    <div className="w-64 bg-slate-800 text-white min-h-screen flex flex-col" data-testid="sidebar">
      <div className="p-6 border-b border-slate-700">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-emerald-600 rounded-lg flex items-center justify-center">
            <Users className="w-6 h-6" />
          </div>
          <h1 className="text-xl font-bold">IS2P Admin</h1>
        </div>
      </div>
      
      <nav className="flex-1 p-4 space-y-2">
        {menuItems.map((item) => {
          const Icon = item.icon;
          const isActive = currentMenu === item.id;
          return (
            <button
              key={item.id}
              onClick={() => onMenuChange(item.id)}
              className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-all ${
                isActive
                  ? "bg-emerald-600 text-white shadow-lg"
                  : "text-slate-300 hover:bg-slate-700 hover:text-white"
              }`}
              data-testid={`menu-${item.id}`}
            >
              <Icon className="w-5 h-5" />
              <span className="font-medium">{item.label}</span>
            </button>
          );
        })}
      </nav>

      <div className="p-4 border-t border-slate-700">
        <button
          onClick={onLogout}
          className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-slate-300 hover:bg-red-600 hover:text-white transition-all"
          data-testid="logout-button"
        >
          <LogOut className="w-5 h-5" />
          <span className="font-medium">Logout</span>
        </button>
      </div>
    </div>
  );
};

const UserDetailsModal = ({ user, isOpen, onClose }) => {
  if (!user) return null;

  const userFields = [
    { label: "Full Name", value: user.fullName },
    { label: "Email Address", value: user.email },
    { label: "Phone Number", value: user.phone },
    { label: "Organization", value: user.organization },
    { label: "Role / Job Title", value: user.role },
    { label: "Industry", value: user.industry },
    { label: "Membership Type", value: user.membershipType },
    { label: "Years of Experience", value: user.yearsExperience },
    { label: "Registration Date", value: user.registeredDate },
    { label: "Payment Status", value: user.paymentStatus },
  ];

  // Generate ID number based on user id
  const idNumber = `IS2P-2024-${String(user.id).padStart(3, '0')}`;
  
  // Calculate expiry date (1 year from registration)
  const registrationDate = new Date(user.registeredDate);
  const expiryDate = new Date(registrationDate);
  expiryDate.setFullYear(expiryDate.getFullYear() + 1);
  const formattedExpiry = `${String(expiryDate.getDate()).padStart(2, '0')}/${String(expiryDate.getMonth() + 1).padStart(2, '0')}/${expiryDate.getFullYear()}`;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto" data-testid="user-details-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-slate-800">Member Details</DialogTitle>
          <DialogDescription>Complete registration information and official membership card</DialogDescription>
        </DialogHeader>
        
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 mt-6">
          {/* Left Column - ID Card */}
          <div className="lg:col-span-2 flex items-start justify-center">
            <div className="w-full max-w-[320px]">
              {/* ID Card with visible border - remove overflow hidden to show bottom */}
              <div className="relative bg-gradient-to-br from-emerald-600 via-emerald-700 to-teal-800 rounded-2xl shadow-2xl border-4 border-emerald-900/30" style={{ minHeight: '520px' }}>
                {/* Background Pattern */}
                <div className="absolute inset-0 opacity-10 overflow-hidden rounded-2xl">
                  <div className="absolute top-0 right-0 w-40 h-40 bg-white rounded-full -translate-y-20 translate-x-20"></div>
                  <div className="absolute bottom-0 left-0 w-32 h-32 bg-white rounded-full translate-y-16 -translate-x-16"></div>
                </div>
                
                {/* Inner border for more definition */}
                <div className="absolute inset-2 border border-white/10 rounded-xl pointer-events-none"></div>
                
                {/* Card Content */}
                <div className="relative h-full flex flex-col p-6">
                  {/* Header */}
                  <div className="text-center mb-4">
                    <div className="inline-flex items-center justify-center w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl mb-2 shadow-lg">
                      <Users className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="text-white font-bold text-sm tracking-wider">IS2P OFFICIAL MEMBER</h3>
                    <p className="text-emerald-100 text-xs mt-1">Indonesian Society of Sustainability Professionals</p>
                  </div>

                  {/* Profile Photo */}
                  <div className="flex justify-center mb-4">
                    <div className="relative">
                      <div className="w-24 h-24 rounded-full bg-white/90 flex items-center justify-center ring-4 ring-white/30 shadow-lg">
                        <Users className="w-14 h-14 text-emerald-600" />
                      </div>
                    </div>
                  </div>

                  {/* Member Name */}
                  <div className="text-center mb-4">
                    <h2 className="text-white font-bold text-xl tracking-wide leading-tight mb-1">
                      {user.fullName.toUpperCase()}
                    </h2>
                    <p className="text-emerald-50 text-sm font-medium">{user.role}</p>
                  </div>

                  {/* ID Number */}
                  <div className="bg-white/15 backdrop-blur-sm rounded-lg p-3 mb-3 shadow-md">
                    <p className="text-emerald-100 text-xs font-semibold mb-1">ID Number</p>
                    <p className="text-white font-bold text-base tracking-wider">{idNumber}</p>
                  </div>

                  {/* Spacer */}
                  <div className="flex-1 min-h-[15px]"></div>

                  {/* Expiry Date - Enhanced visibility with more contrast and padding */}
                  <div className="bg-emerald-900/40 backdrop-blur-sm rounded-lg p-4 border-2 border-white/40 shadow-xl">
                    <p className="text-white text-xs font-bold uppercase tracking-wider mb-2">Active Until</p>
                    <p className="text-white font-bold text-xl tracking-wide">{formattedExpiry}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Member Information */}
          <div className="lg:col-span-3">
            <div className="space-y-1">
              <h3 className="text-lg font-bold text-slate-800 mb-4 pb-2 border-b-2 border-emerald-600">Registration Information</h3>
              {userFields.map((field, index) => (
                <div key={index} className="grid grid-cols-5 gap-4 py-3 border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors rounded px-2">
                  <div className="col-span-2 font-semibold text-slate-700 text-sm">{field.label}</div>
                  <div className="col-span-3 text-slate-900">
                    {field.label === "Payment Status" ? (
                      <Badge
                        className={`${
                          field.value === "Success"
                            ? "bg-emerald-100 text-emerald-700 border-emerald-300"
                            : field.value === "Pending"
                            ? "bg-amber-100 text-amber-700 border-amber-300"
                            : "bg-red-100 text-red-700 border-red-300"
                        }`}
                      >
                        {field.value}
                      </Badge>
                    ) : (
                      <span className="text-sm">{field.value}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const DataUserView = () => {
  const [selectedUser, setSelectedUser] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleViewDetails = (user) => {
    setSelectedUser(user);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setTimeout(() => setSelectedUser(null), 200);
  };

  const totalUsers = mockUsers.length;
  const pendingPayments = mockUsers.filter(u => u.paymentStatus === "Pending").length;
  const activeMembers = mockUsers.filter(u => u.paymentStatus === "Success").length;

  return (
    <div className="p-8 space-y-6" data-testid="data-user-view">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold text-slate-800">User Membership Management</h2>
          <p className="text-slate-600 mt-1">Manage and monitor all member registrations</p>
        </div>
        <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="export-csv-button">
          <FileDown className="w-4 h-4 mr-2" />
          Export to CSV
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="border-l-4 border-l-emerald-600 shadow-sm" data-testid="stat-total-users">
          <CardHeader className="pb-3">
            <CardDescription className="text-slate-600">Total Users</CardDescription>
            <CardTitle className="text-4xl font-bold text-slate-800">{totalUsers}</CardTitle>
          </CardHeader>
        </Card>
        <Card className="border-l-4 border-l-amber-500 shadow-sm" data-testid="stat-pending-payments">
          <CardHeader className="pb-3">
            <CardDescription className="text-slate-600">Pending Payments</CardDescription>
            <CardTitle className="text-4xl font-bold text-slate-800">{pendingPayments}</CardTitle>
          </CardHeader>
        </Card>
        <Card className="border-l-4 border-l-blue-600 shadow-sm" data-testid="stat-active-members">
          <CardHeader className="pb-3">
            <CardDescription className="text-slate-600">Active Members</CardDescription>
            <CardTitle className="text-4xl font-bold text-slate-800">{activeMembers}</CardTitle>
          </CardHeader>
        </Card>
      </div>

      {/* Data Table */}
      <Card className="shadow-lg" data-testid="users-table-card">
        <CardHeader>
          <CardTitle className="text-xl text-slate-800">Member Directory</CardTitle>
          <CardDescription>All registered members and their current status</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="font-bold text-slate-700">Full Name</TableHead>
                  <TableHead className="font-bold text-slate-700">Email</TableHead>
                  <TableHead className="font-bold text-slate-700">Organization</TableHead>
                  <TableHead className="font-bold text-slate-700">Role / Job Title</TableHead>
                  <TableHead className="font-bold text-slate-700">Payment Status</TableHead>
                  <TableHead className="font-bold text-slate-700 text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {mockUsers.map((user) => (
                  <TableRow key={user.id} className="hover:bg-slate-50 transition-colors" data-testid={`user-row-${user.id}`}>
                    <TableCell className="font-medium text-slate-800">{user.fullName}</TableCell>
                    <TableCell className="text-slate-600">{user.email}</TableCell>
                    <TableCell className="text-slate-600">{user.organization}</TableCell>
                    <TableCell className="text-slate-600">{user.role}</TableCell>
                    <TableCell>
                      <Badge
                        className={`${
                          user.paymentStatus === "Success"
                            ? "bg-emerald-100 text-emerald-700 border-emerald-300"
                            : user.paymentStatus === "Pending"
                            ? "bg-amber-100 text-amber-700 border-amber-300"
                            : "bg-red-100 text-red-700 border-red-300"
                        }`}
                        data-testid={`payment-status-${user.id}`}
                      >
                        {user.paymentStatus}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleViewDetails(user)}
                        className="hover:bg-emerald-50 hover:text-emerald-700"
                        data-testid={`view-details-${user.id}`}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <UserDetailsModal user={selectedUser} isOpen={isModalOpen} onClose={handleCloseModal} />
    </div>
  );
};

const DashboardView = () => {
  return (
    <div className="p-8 space-y-6" data-testid="dashboard-view">
      <h2 className="text-3xl font-bold text-slate-800">Dashboard</h2>
      <Card className="shadow-lg">
        <CardHeader>
          <CardTitle>Welcome to IS2P Admin Dashboard</CardTitle>
          <CardDescription>This view is under construction</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-slate-600">Dashboard analytics and overview will be displayed here.</p>
        </CardContent>
      </Card>
    </div>
  );
};

const SettingsView = () => {
  return (
    <div className="p-8 space-y-6" data-testid="settings-view">
      <h2 className="text-3xl font-bold text-slate-800">Settings</h2>
      <Card className="shadow-lg">
        <CardHeader>
          <CardTitle>System Settings</CardTitle>
          <CardDescription>This view is under construction</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-slate-600">Configuration and settings options will be displayed here.</p>
        </CardContent>
      </Card>
    </div>
  );
};

function App() {
  const [isAdminLoggedIn, setIsAdminLoggedIn] = useState(false);
  const [currentMenu, setCurrentMenu] = useState("users");

  const handleLogin = () => {
    setIsAdminLoggedIn(true);
  };

  const handleLogout = () => {
    setIsAdminLoggedIn(false);
    setCurrentMenu("users");
  };

  if (!isAdminLoggedIn) {
    return <LoginPage onLogin={handleLogin} />;
  }

  return (
    <div className="flex min-h-screen bg-slate-50" data-testid="admin-dashboard">
      <Sidebar currentMenu={currentMenu} onMenuChange={setCurrentMenu} onLogout={handleLogout} />
      <div className="flex-1 overflow-x-hidden">
        {currentMenu === "dashboard" && <DashboardView />}
        {currentMenu === "users" && <DataUserView />}
        {currentMenu === "settings" && <SettingsView />}
      </div>
    </div>
  );
}

export default App;
